import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { useBreadcrumb } from '../context/BreadcrumbContext';

interface BreadcrumbItem {
  label: string;
  path?: string;
  isCurrent?: boolean;
}

interface BreadcrumbProps {
  items?: BreadcrumbItem[];
  className?: string;
}


const Breadcrumb: React.FC<BreadcrumbProps> = ({ items, className = '' }) => {
  const location = useLocation();
  const { labels } = useBreadcrumb();

  // If no items provided, generate basic ones from path
  const breadcrumbs: BreadcrumbItem[] = items || (() => {
    const pathnames = location.pathname.split('/').filter((x) => x);

    // Member pages that should show Profile as parent
    const memberPages: Record<string, string> = {
      'favorites': 'Favorites',
      'notes': 'Notes',
      'preferences': 'Preferences',
    };

    // Legal pages that should show Start Here! as parent
    const legalPages: Record<string, string> = {
      'privacy-policy': 'Privacy Policy',
      'terms-of-service': 'Terms of Service',
    };

    // Check if current page is a member page
    const firstSegment = pathnames[0];
    if (firstSegment && memberPages[firstSegment] && pathnames.length === 1) {
      return [
        { label: 'Home', path: '/', isCurrent: false },
        { label: 'Profile', path: '/profile', isCurrent: false },
        { label: memberPages[firstSegment], path: `/${firstSegment}`, isCurrent: true }
      ];
    }

    // Check if current page is a legal page
    if (firstSegment && legalPages[firstSegment] && pathnames.length === 1) {
      return [
        { label: 'Home', path: '/', isCurrent: false },
        { label: 'Start Here!', path: '/starthere', isCurrent: false },
        { label: legalPages[firstSegment], path: `/${firstSegment}`, isCurrent: true }
      ];
    }

    return [
      { label: 'Home', path: '/', isCurrent: pathnames.length === 0 },
      ...pathnames.map((name, index) => {
        const routeTo = `/${pathnames.slice(0, index + 1).join('/')}`;
        const isLast = index === pathnames.length - 1;

        // Use mapping if available, otherwise capitalize
        const label = labels[name] || name.charAt(0).toUpperCase() + name.slice(1);

        return {
          label: decodeURIComponent(label),
          path: routeTo,
          isCurrent: isLast
        };
      })
    ];
  })();

  return (
    <nav className={`flex pt-2 pb-1 sm:pt-3 sm:pb-1 md:pt-4 md:pb-1 px-4 sm:px-6 md:px-8 text-sm sm:text-base md:text-lg font-bold overflow-x-auto whitespace-nowrap min-h-[44px] ${className}`} aria-label="Breadcrumb">
      <ol className="inline-flex items-center space-x-1 sm:space-x-2 md:space-x-3">
        {breadcrumbs.map((item, index) => (
          <li key={index} className="inline-flex items-center">
            {index > 0 && (
              <ChevronRight className="w-5 h-5 sm:w-6 sm:h-6 md:w-7 md:h-7 text-yellow-300 mx-2" />
            )}

            {item.label === 'Home' ? (
              // Home is always bright gold
              item.isCurrent || !item.path ? (
                <span className="font-bold text-yellow-300">
                  {item.label}
                </span>
              ) : (
                <Link
                  to={item.path}
                  className="font-bold text-yellow-300 hover:text-yellow-200 hover:underline transition-colors"
                >
                  {item.label}
                </Link>
              )
            ) : item.isCurrent || !item.path ? (
              // Current location (non-Home) in brighter color
              <span className="font-bold text-yellow-200">
                {item.label}
              </span>
            ) : (
              // Navigation links in bright color
              <Link
                to={item.path}
                className="font-bold text-slate-300 hover:text-yellow-200 hover:underline transition-colors"
              >
                {item.label}
              </Link>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
};

export default Breadcrumb;
